import BrowserBroadcastChannelService from './BrowserBroadcastChannelService.js'

class ChatSyncService {
  constructor() {
    this.broadcastService = BrowserBroadcastChannelService.getInstance()
    this.messageListeners = new Set()

    this.boundHandleMessageSent = this.handleMessageSent.bind(this)
    this.boundHandleMessagesRead = this.handleMessagesRead.bind(this)

    // Subscribe to chat-related messages
    this.broadcastService.subscribe(
      'USER_MESSAGE_SENT',
      this.boundHandleMessageSent
    )
    this.broadcastService.subscribe(
      'MESSAGES_READ',
      this.boundHandleMessagesRead
    )
  }

  /**
   * Broadcast that a message was sent from this tab
   * @param {Object} messageData - { conversationId, message, timestamp, etc. }
   */
  broadcastMessageSent(messageData) {
    this.broadcastService.broadcast('USER_MESSAGE_SENT', {
      ...messageData,
      fromTabId: this.broadcastService.tabId
    })
  }

  /**
   * Handle incoming message from other tabs
   * @param {Object} data - Message data from broadcast
   */
  handleMessageSent(data) {
    // Only process messages from other tabs
    if (data.fromTabId === this.broadcastService.tabId) {
      return
    }

    this.notifyListeners('USER_MESSAGE_SENT', data)
  }

  handleMessagesRead(data) {
    if (data.fromTabId === this.broadcastService.tabId) {
      return
    }

    this.notifyListeners('MESSAGES_READ', data)
  }

  notifyListeners(type, data) {
    for (const listener of this.messageListeners) {
      try {
        listener({ type, data })
      } catch (error) {
        console.error('Error in message listener:', error)
      }
    }
  }

  /**
   * Register a callback to be called when a message is received from another tab
   * @param {Function} callback - Function to call with message data
   * @returns {Function} - Unsubscribe function
   */
  onMessageReceived(callback) {
    this.messageListeners.add(callback)
    return () => this.messageListeners.delete(callback)
  }

  /**
   * Legacy method for backwards compatibility
   * @param {Function} callback - Function to call with message data
   * @returns {Function} - Unsubscribe function
   */
  listen(callback) {
    return this.onMessageReceived(callback)
  }

  /**
   * Broadcast typing status
   * @param {string} conversationId
   * @param {boolean} isTyping
   */
  broadcastTypingStatus(conversationId, isTyping) {
    this.broadcastService.broadcast('USER_TYPING', {
      conversationId,
      isTyping,
      fromTabId: this.broadcastService.tabId
    })
  }

  /**
   * Broadcast that user read messages in a conversation
   * @param {Object} payload
   * @param {string} payload.userId
   * @param {string} payload.conversationId
   * @param {Array} payload.messageIds - only messages actually acknowledged
   * @param {string} payload.seenAt
   */
  broadcastMessagesRead({ userId, conversationId, messageIds, seenAt }) {
    if (!userId || !conversationId || !messageIds?.length) return

    this.broadcastService.broadcast('MESSAGES_READ', {
      conversationId,
      userId,
      messageIds,
      seenAt,
      fromTabId: this.broadcastService.tabId
    })
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.messageListeners.clear()
    // Unsubscribe from broadcast service
    this.broadcastService.unsubscribe(
      'USER_MESSAGE_SENT',
      this.boundHandleMessageSent
    )
    this.broadcastService.unsubscribe(
      'MESSAGES_READ',
      this.boundHandleMessagesRead
    )
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!ChatSyncService.instance) {
      ChatSyncService.instance = new ChatSyncService()
    }
    return ChatSyncService.instance
  }
}

export default ChatSyncService
